import React from "react"
import { Avatar, AvatarImage } from "../../../shared/components/ui/avatar"
import { AvatarFallback } from "@radix-ui/react-avatar"
import { formatPhoneNumber, shortenName } from "@/shared/lib/utils"
import OwnerInfoMobile from "@/features/homeowners/components/owner-info-mobile"
import { formatDate } from "date-fns"
import { Volunteer, NonHomeownerUser, VolunteerTableRow } from "../types/volunteers.type"
import EditUserButton, { UpdateUserLink } from "./edit-user-button"
import DeleteUserButton from "./delete-user-button"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"

// Union type for mobile card data
type MobileCardDataItem = Volunteer | NonHomeownerUser | VolunteerTableRow

// Helper function to parse a date value (handles YYYY-MM-DD and ISO strings)
const parseDateValue = (dateValue: string | Date | undefined | null): Date | null => {
    if (!dateValue) return null
    
    if (dateValue instanceof Date) {
        return isNaN(dateValue.getTime()) ? null : dateValue
    }
    
    if (typeof dateValue === 'string') {
        // If it's a date-only string (YYYY-MM-DD), parse as local time
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
            const [year, month, day] = dateValue.split('-').map(Number)
            const date = new Date(year, month - 1, day)
            return isNaN(date.getTime()) ? null : date
        }
        // ISO string with time, parse normally
        const date = new Date(dateValue)
        return isNaN(date.getTime()) ? null : date
    }
    
    return null
}

// Helper functions to extract data from both types
const getUserData = (item: MobileCardDataItem) => {
    return 'user' in item ? item.user : item
}

const getVolunteerData = (item: MobileCardDataItem) => {
    if ('user' in item) {
        // This is a Volunteer type
        return {
            organizationName: item.organizationName ?? null,
        }
    } else {
        // This is NonHomeownerUser or VolunteerTableRow
        const v = item.volunteer as { organizationName?: string | null } | undefined
        return {
            organizationName: v?.organizationName ?? ('organizationName' in item ? item.organizationName : null) ?? null,
        }
    }
}

function VolunteerCardMobile({ item }: { item: MobileCardDataItem }) {
    const user = getUserData(item)
    const volunteerData = getVolunteerData(item)
    const { session } = useSession()
    const canEditDelete = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
    const lastActiveDate = user.lastActiveAt ? parseDateValue(user.lastActiveAt) : null
    
    return (
        <div className="border p-3 space-y-4 rounded">
            <div className="flex justify-between gap-1">
                <div className="space-y-1">
                    <h3 className="text-muted-foreground text-xs">Name</h3>
                    <div className="flex gap-x-1">
                        <Avatar className="flex items-center justify-center bg-primary text-white w-9 h-9">
                            <AvatarImage
                                src={user.profilePicture ?? undefined}
                            />
                            <AvatarFallback>
                                {shortenName(user.fullname)}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col justify-center font-medium">
                            {canEditDelete ? (
                                <UpdateUserLink user={item}>
                                    <span className="text-sm">{user.fullname}</span>
                                </UpdateUserLink>
                            ) : (
                                <p className="text-foreground text-sm">{user.fullname}</p>
                            )}
                            <small className="text-muted-foreground text-[11px]">
                                {user.role.name}
                            </small>
                        </div>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-y-5 gap-x-2">
                <OwnerInfoMobile
                    infoTitle="Organization"
                    infoValue={volunteerData.organizationName ?? "N/A"}
                />
                <OwnerInfoMobile
                    infoTitle="Role"
                    infoValue={user.role.name}
                />
                <OwnerInfoMobile
                    infoTitle="Phone Number"
                    infoValue={
                        user.phone
                            ? formatPhoneNumber(user.phone)
                            : "N/A"
                    }
                />
                <OwnerInfoMobile
                    infoTitle="Last Active"
                    infoValue={
                        lastActiveDate
                            ? formatDate(lastActiveDate, "dd-MMM-yyyy")
                            : "N/A"
                    }
                />
                <div className="col-span-2 min-w-0">
                    <h3 className="text-muted-foreground text-xs font-medium">Email</h3>
                    <p className="text-foreground text-sm break-all">
                        {user.email?.trim() ? user.email : "N/A"}
                    </p>
                </div>
            </div>
            {canEditDelete && (
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 ml-auto">
                        <EditUserButton user={item} />
                        <DeleteUserButton user={item} />
                    </div>
                </div>
            )}
        </div>
    )
}

export default VolunteerCardMobile
